<template>
  <div class="blogposts-container my-3 pb-3">
    <h1 class="text-center">Blog</h1>
    <div class="mx-3 mt-3">
      <router-link to="/subscribe">
        <button type="button"
                class="btn btn-outline-light w-100 mb-3 subscribe-btn"
                data-bs-theme="dark">
          Subscribe
        </button>
      </router-link>
    </div>
    <div class="blogposts-list">
      <div v-for="(post, idx) in posts" :key="post.link">
        <router-link :to="'/blog/' + post.link" class="blogpost-link">
          <div class="blogpost-row d-flex flex-column flex-md-row align-items-start align-items-md-center py-3">
            <div class="flex-grow-1">
              <h3 class="blogpost-title mb-1">{{post.name}}</h3>
              <p class="blogpost-desc mb-0"><i>{{post.description}}</i></p>
              <div class="blogpost-tags mt-2">
                <span v-for="tag in post.tags" :key="tag" class="blogpost-tag" :style="{ backgroundColor: tagColors[tag] }">{{ tag }}</span>
              </div>
            </div>
            <div class="blogpost-date flex-shrink-0 ms-0 ms-md-3 mt-2 mt-md-0 text-center text-nowrap">{{post.date}}</div>
          </div>
        </router-link>
        <div v-if="idx < posts.length - 1" class="blogpost-divider"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      tagColors: {
        'Physics': '#34495E',         // darker steel blue
        'Management': '#512E5F',      // dark purple
        'IT': '#6D4C41',              // darker taupe
        'Product management': '#7D6608', // dark ochre
        'Finance': '#145A32',         // dark green
        'Music': '#4E342E',           // dark brown
        'Mindset': '#512E1A',         // dark maroon
        'Python': '#00695C',          // dark teal
        'git': '#7B241C',             // dark red
        'Energy': '#37474F',          // dark slate
        'AI': '#4B1166',              // dark violet
        'Education': '#7B133B',       // dark magenta
        'Science': '#00596B',         // dark cyan
      },
      posts: [
        {
          name: '"Nothing" is the secret to structuring your work',
          description: 'Using work surfaces in your professional and daily life.',
          link: 'nothing',
          date: '7 Feb. 2026',
          tags: ['Mindset', 'Management', 'IT'],
        },
        {
          name: 'Simplifying the risk-neutral pricing argument',
          description: 'Demystifying risk-neutral pricing with linear algebra and probability theory.',
          link: 'simplifying-the-risk-neutral-pricing-argument',
          date: '25 Sep. 2025',
          tags: ['Education', 'Finance', 'Science'],
        },
        {
          name: 'Why the term ½σ² keeps popping up in financial mathematics',
          description: 'A hand-waving, playful journey through math, portfolios, and the volatility penalty.',
          link: 'why-the-term-half-sigma-squared-keeps-popping-up-in-financial-mathematics',
          date: '13 Sep. 2025',
          tags: ['Education', 'Finance', 'Science'],
        },
        {
          name: 'A hand-waving derivation of the relation between entropy and temperature',
          description: 'Deriving the relation between entropy and temperature using statistical mechanics and a toy model.',
          link: 'a-hand-waving-derivation-of-the-relation-between-entropy-and-temperature',
          date: '5 Sep. 2025',
          tags: ['Education', 'Physics', 'Science'],
        },
        {
          name: 'How the random walk converges to a normal distribution',
          description: 'A textbook derivation with some clever tricks and classical theorems.',
          link: 'how-the-random-walk-converges-to-a-normal-distribution',
          date: '23 Aug. 2025',
          tags: ['Education', 'Science'],
        },
        {
          name: 'The motivation paradox',
          description: 'Why I made Developer Badges.',
          link: 'the-motivation-paradox',
          date: '22 Mar. 2025',
          tags: ['Management', 'IT'],
        },
        {
          name: 'Badges for software developers',
          description: 'A playful list of badges for software developers.',
          link: 'badges-for-software-developers',
          date: '2 Mar. 2025',
          tags: ['Management', 'IT'],
        },
        {
          name: 'Unit testing and double-entry bookkeeping',
          description: 'Be like the Medicis.',
          link: 'unit-testing-and-double-entry-bookkeeping',
          date: '7 Feb. 2025',
          tags: ['IT', 'Finance'],
        },
        {
          name: 'Chanto',
          description: 'What we can learn from the Japanese.',
          link: 'chanto-or-what-we-can-learn-from-the-japanese',
          date: '26 Jan. 2025',
          tags: ['Mindset', 'Product management'],
        },
        {
          name: 'Hiring a developer for my band',
          description: 'Why software developers are closer to artists than to factory workers.',
          link: 'hiring-a-developer-for-my-band',
          date: '4 Dec. 2024',
          tags: ['Music', 'IT', 'Management'],
        },
        {
          name: 'The three pillars of a bug report',
          description: 'A mental framework to structure bugfixing.',
          link: 'the-three-pillars-of-a-bug-report',
          date: '9 Nov. 2024',
          tags: ['IT', 'Product management'],
        },
        {
          name: 'What is money and how does "money printing" work?',
          description: 'The most basic explanation of how money works, focused on the euro currency.',
          link: 'what-is-money-and-how-does-money-printing-work',
          date: '17 Sep. 2024',
          tags: ['Education', 'Finance'],
        },
        {
          name: 'Lessons learnt building a real-time audio application in Python',
          description: 'Three lessons learnt while building Backlooper.app.',
          link: 'lessons-learnt-backlooper',
          date: '7 Sep. 2024',
          tags: ['Python', 'Music', 'IT'],
        },
        {
          name: 'The big list of unelaborate statements',
          description: 'A list of unelaborate statements about building software.',
          link: 'the-big-list-of-unelaborate-statements',
          date: '17 Mar. 2024',
          tags: ['Mindset', 'IT', 'Product management'],
        },
        {
          name: 'When cheap energy turns green',
          description: 'Does cheap power mean green power?',
          link: 'when-cheap-energy-turns-green',
          date: '2 Dec. 2023',
          tags: ['Energy', 'Science'],
        },
        {
          name: 'Robotaxis and AI-hardness',
          description: 'In AI, some problems are harder than others.',
          link: 'robotaxis-and-AI-hardness',
          date: '16 Jan. 2022',
          tags: ['IT', 'AI'],
        },
        {
          name: 'Adding confidence intervals to maximum likelihood estimates using sympy',
          description: 'Not pretty, but interesting.',
          link: 'mle-ci',
          date: '2 Jun. 2021',
          tags: ['Python', 'Science'],
        },
        {
          name: 'Thinking in orders of magnitude',
          description: 'Where physicists have an edge over computer scientists.',
          link: 'orders-of-magnitude',
          date: '23 Jan. 2021',
          tags: ['Physics', 'Science'],
        },
        {
          name: 'How, why and the smelly washing machine',
          description: 'Why self-documenting code does not exist.',
          link: 'how-why-and-the-smelly-washing-machine',
          date: '2 Nov. 2020',
          tags: ['Mindset', 'IT', 'Python'],
        },
        {
          name: 'Datetimes and DST',
          description: 'Not as simple as it looks, but not rocket science either.',
          link: 'datetimes-and-dst',
          date: '17 Oct. 2020',
          tags: ['Python', 'IT', 'Education'],
        },
        {
          name: 'The number of bugs grows exponentially',
          description: 'How the number of bugs can grow exponentially and what can be done about it.',
          link: 'number-of-bugs-grows-exponentially',
          date: '8 Sep. 2020',
          tags: ['IT', 'Science', 'Python'],
        },
        {
          name: 'Strings, bytes and unicode',
          description: 'There tends to be confusion about strings, bytes and unicode in Python 2 vs. 3. This article explains that it is not as confusing as it might seem.',
          link: 'strings-bytes-unicode',
          date: '22 Apr. 2020',
          tags: ['Python', 'IT', 'Education'],
        },
        {
          name: 'Intro to git',
          description: 'Introduction to git, concluding with intermediate examples.',
          link: 'intro-to-git',
          date: '13 Mar. 2019',
          tags: ['git', 'IT', 'Education'],
        },
      ]
    }
  },
  created () {
    document.title = "Blog"
  }
}
</script>

<style scoped>
  .blogposts-container {
    margin: 0 auto;
    background: rgb(43, 43, 43);
    border-radius: 16px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.12);
    padding: 24px 0 8px 0;
  }
  .blogposts-list {
    width: 100%;
  }
  .blogpost-row {
    transition: background 0.15s;
    padding-left: 24px;
    padding-right: 24px;
    cursor: pointer;
  }
  .blogpost-row:hover {
    background: rgb(30, 30, 30);
  }
  .blogpost-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
    text-align: left;
  }
  .blogpost-desc {
    font-size: 1rem;
    color: #ccc;
    text-align: left;
  }
  .blogpost-date {
    font-size: 0.95rem;
    color: #999;
    text-align: right;
  }
  @media (max-width: 767.98px) {
    .blogpost-row.flex-column .blogpost-date {
      text-align: center !important;
      width: 100%;
    }
  }
  .blogpost-divider {
    height: 1px;
    background: #555;
    margin: 0 24px;
  }
  .subscribe-btn {
    font-family: "Open Sans", sans-serif;
    background: rgb(30, 30, 30);
    border: none;
    font-size: 1.2rem;
    color: #eee;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    border-radius: 8px;
    transition: background 0.2s;
  }
  .subscribe-btn:hover {
    background: rgb(60, 63, 65);
    color: #eee;
  }
  a.blogpost-link {
    color: inherit;
    text-decoration: none;
    display: block;
  }
  .blogpost-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 0.5rem;
  }
  .blogpost-tag {
    font-size: 0.9rem;
    font-weight: 500;
    color: #fff;
    padding: 4px 8px;
    border-radius: 12px;
    display: inline-block;
  }
</style>